import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Space,
  Typography,
  message,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import * as XLSX from "xlsx";
import axAPI from "../../../api/axios-instance";

type ExportGroup = {
  leagueId: number;
  leagueTitle: string;
  seasonId: number;
  seasonTitle: string;
  groupId: number | null;
  groupTitle: string;
};

type ExportRow = {
  leagueId: number;
  leagueTitle: string;
  groupTitle: string;
  tour: string | null;
  date: string | null;
  team1: string | null;
  team2: string | null;
  url: string | null;
};

// Хронометраж трансляции в минутах: Первая лига — 150, Вторая А и Б — 135.
const durationByLeague = (leagueId: number) => (leagueId === 100 ? 150 : 135);

const groupKey = (g: ExportGroup) =>
  `${g.leagueId}:${g.seasonId}:${g.groupId ?? ""}`;
// Сезон в подписи показываем только когда в период попали два сезона одной лиги
// (например, конец 2025 и начало 2026 у Второй лиги Б — группы у них разные).
const groupLabel = (g: ExportGroup, showSeason: boolean) => {
  const base =
    g.groupId === null ? g.leagueTitle : `${g.leagueTitle} ${g.groupTitle}`;
  return showSeason ? `${base} (${g.seasonTitle})` : base;
};

export function BroadcastExport() {
  const [groups, setGroups] = useState<ExportGroup[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [loading, setLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);

  const dateFrom = range?.[0]?.format("YYYY-MM-DD");
  const dateTo = range?.[1]?.format("YYYY-MM-DD");

  // Чек-лист зависит от периода: группы у лиг меняются от сезона к сезону
  // (у Второй лиги Б в 2025 — 1А/1Б, в 2026 — 1–4), поэтому список
  // запрашиваем под выбранные даты, а не по текущему сезону.
  useEffect(() => {
    setSelected([]);
    if (!dateFrom || !dateTo) {
      setGroups([]);
      return;
    }
    setGroupsLoading(true);
    axAPI
      .get<ExportGroup[]>("cms/broadcast/export/groups", {
        params: { dateFrom, dateTo },
      })
      .then((res) => setGroups(res.data ?? []))
      .catch(() => message.error("Не удалось загрузить список лиг и групп"))
      .finally(() => setGroupsLoading(false));
  }, [dateFrom, dateTo]);

  const options = useMemo(() => {
    const seasonsByLeague = new Map<number, Set<number>>();
    groups.forEach((g) => {
      if (!seasonsByLeague.has(g.leagueId))
        seasonsByLeague.set(g.leagueId, new Set());
      seasonsByLeague.get(g.leagueId)!.add(g.seasonId);
    });
    return groups.map((g) => ({
      value: groupKey(g),
      label: groupLabel(g, (seasonsByLeague.get(g.leagueId)?.size ?? 0) > 1),
    }));
  }, [groups]);

  const canExport = selected.length > 0 && !!dateFrom && !!dateTo;

  const handleExport = async () => {
    if (!canExport || !dateFrom || !dateTo) return;
    const items = groups
      .filter((g) => selected.includes(groupKey(g)))
      .map((g) => ({
        leagueId: g.leagueId,
        seasonId: g.seasonId,
        groupId: g.groupId,
      }));
    setLoading(true);
    try {
      const res = await axAPI.post<ExportRow[]>("cms/broadcast/export", {
        items,
        dateFrom,
        dateTo,
      });
      const rows = res.data ?? [];
      if (rows.length === 0) {
        message.info("Матчей за выбранный период нет");
        return;
      }
      buildXlsx(rows, dateFrom, dateTo);
      message.success(`Выгружено матчей: ${rows.length}`);
    } catch {
      message.error("Не удалось сформировать выгрузку");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card title="Экспорт ссылок на матчи">
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Typography.Text strong>Период</Typography.Text>
          <div style={{ marginTop: 8 }}>
            <DatePicker.RangePicker
              value={range}
              onChange={(v) => setRange(v)}
              format="DD.MM.YYYY"
              placeholder={["Дата от", "Дата до"]}
            />
          </div>
        </div>
        <div>
          <Typography.Text strong>Лиги и группы</Typography.Text>
          <div style={{ marginTop: 8 }}>
            {!dateFrom || !dateTo ? (
              <Typography.Text type="secondary">
                Сначала выберите период
              </Typography.Text>
            ) : groupsLoading ? (
              <Typography.Text type="secondary">Загрузка…</Typography.Text>
            ) : options.length === 0 ? (
              <Typography.Text type="secondary">
                Матчей за выбранный период нет
              </Typography.Text>
            ) : null}
            <Checkbox.Group
              value={selected}
              onChange={(v) => setSelected(v as string[])}
              style={{ display: "flex", flexDirection: "column", gap: 6 }}
            >
              {options.map((o) => (
                <Checkbox key={o.value} value={o.value}>
                  {o.label}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
          <Space style={{ marginTop: 8 }}>
            <Button
              size="small"
              onClick={() => setSelected(options.map((o) => o.value))}
            >
              Выбрать все
            </Button>
            <Button size="small" onClick={() => setSelected([])}>
              Снять все
            </Button>
          </Space>
        </div>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          disabled={!canExport}
          loading={loading}
          onClick={handleExport}
        >
          Сформировать xlsx
        </Button>
      </Space>
    </Card>
  );
}

function buildXlsx(rows: ExportRow[], dateFrom: string, dateTo: string) {
  const header = [
    "Дата матча",
    "Группа",
    "Тур",
    "Время начала",
    "Команда хозяев",
    "Команда гостей",
    "Ссылка на трансляцию в VK",
    "Хронометраж",
    "Время окончания",
    "Матч",
    "Отчёт",
  ];
  const data = rows.map((r) => {
    const start = r.date ? dayjs(r.date) : null;
    const duration = durationByLeague(r.leagueId);
    const end = start ? start.add(duration, "minute") : null;
    const group =
      r.groupTitle === r.leagueTitle
        ? r.leagueTitle
        : `${r.leagueTitle} ${r.groupTitle}`;
    const match = `${r.team1 ?? ""} - ${r.team2 ?? ""}`;
    const dateStr = start ? start.format("DD.MM.YYYY") : "";
    const startStr = start ? start.format("HH:mm") : "";
    const endStr = end ? end.format("HH:mm") : "";
    const report = r.url
      ? `Трансляция Матча под названием «${match}», время проведения Матча с «${startStr}» по «${endStr}», дата проведения: «${dateStr}», хронометражем ${duration} минут, URL-адрес размещения Трансляции: ${r.url}`
      : "";
    return [
      dateStr,
      group,
      r.tour ?? "",
      startStr,
      r.team1 ?? "",
      r.team2 ?? "",
      r.url ?? "",
      duration,
      endStr,
      match,
      report,
    ];
  });

  const sheet = XLSX.utils.aoa_to_sheet([header, ...data]);
  sheet["!cols"] = [12, 28, 10, 12, 22, 22, 50, 12, 14, 40, 120].map((wch) => ({
    wch,
  }));
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "Матчи");
  XLSX.writeFile(book, `Ссылки на трансляции ${dateFrom} - ${dateTo}.xlsx`);
}
